/**
 * StudioVerifyRail — Option C's Fit Verdict rail. Turns the canvas into a
 * go/no-go instrument by re-presenting the solver's containment / setback /
 * reservation output as a checklist, utilization bars and the reservation
 * breakdown. It renders `buildFitVerdict` + `fitReservationRows` (pure
 * projections over the test-fit result) and computes NOTHING itself — the
 * deterministic solver remains the single authority.
 *
 * The interactive Save → Promote → Publish → SED handoff is intentionally NOT
 * duplicated here: it owns workbench state (readiness, publish review) and
 * lives in the Step-6 handoff card. The rail only names it as the next step.
 */
import React, { useMemo } from 'react';
import { LandScreeningBasis } from '../../LandScreeningBasis';
import { AlertTriangle, CheckCircle2, HelpCircle, Info } from 'lucide-react';

import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import type { GenerationMaxPowerStudySnapshot } from '../../../../types/generationImpact';
import {
  buildFitVerdict,
  fitReservationRows,
  type FitCheckState,
  type FitHeadlineState,
} from './studioVerify';

export interface StudioVerifyIssueNavigation {
  checkId: string;
  componentIds: string[];
  fitTarget: 'issues' | 'target-parcel';
}

interface StudioVerifyRailProps {
  testFit: GenerationParcelTestFitResult;
  /** Scenario has no placed equipment — checks rest on default sizing. */
  provisionalBasis?: boolean;
  /** Select and fit the geometry behind a blocking or unknown check. */
  onNavigateIssue?: (issue: StudioVerifyIssueNavigation) => void;
  /**
   * Max-power envelope for the CURRENT parcel + layout. The rail COMPUTES
   * NOTHING — consistent with its standing contract, this is an already-built
   * snapshot passed in by the owner.
   */
  maxPower?: GenerationMaxPowerStudySnapshot | null;
  maxPowerBusy?: boolean;
  onRunMaxPower?: () => void;
  /** Apply the geometry-neutral placement the study found. */
  onApplyMaxPowerPlacement?: (values: Record<string, number>) => void;
  /** Why applying is unavailable. Present ⇒ the action is blocked, and SAYS so
   *  rather than rendering an enabled button that silently does nothing. */
  applyPlacementBlockedReason?: string | null;
}

const STATE_ICON: Record<FitCheckState, React.ReactNode> = {
  pass: <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />,
  warn: <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />,
  unknown: <HelpCircle className="h-4 w-4 text-text-muted" aria-hidden />,
  info: <Info className="h-4 w-4 text-primary" aria-hidden />,
};

const HEADLINE_CLASS: Record<FitHeadlineState, string> = {
  pass: 'border-success/50 bg-success/10 text-success',
  warn: 'border-danger/50 bg-danger/10 text-danger',
  unknown: 'border-warning/50 bg-warning/10 text-warning',
};

/** Clamp a nullable percentage into a bar width string. */
function barWidth(pct: number | null): string {
  if (pct == null) return '0%';
  return `${Math.max(0, Math.min(100, pct))}%`;
}

function issueNavigation(
  testFit: GenerationParcelTestFitResult,
  checkId: string,
): StudioVerifyIssueNavigation {
  const componentIds = testFit.components
    .filter((component) => {
      if (checkId === 'containment') return (component.outsideAcres ?? 0) > 0.01;
      if (checkId === 'setback') return component.meetsParcelEdgeSetback !== true;
      if (checkId === 'exclusions') {
        return component.exclusionOverlapAcres == null || component.exclusionOverlapAcres > 0.01;
      }
      if (checkId === 'collisions') {
        return component.obstacleOverlapAcres == null || component.obstacleOverlapAcres > 0.01;
      }
      return false;
    })
    .map(component => component.rectangle.id);
  return {
    checkId,
    componentIds,
    fitTarget: checkId === 'setback' && componentIds.length === 0
      ? 'target-parcel'
      : 'issues',
  };
}

export const StudioVerifyRail: React.FC<StudioVerifyRailProps> = ({
  testFit,
  provisionalBasis,
  onNavigateIssue,
  maxPower,
  maxPowerBusy,
  onRunMaxPower,
  onApplyMaxPowerPlacement,
  applyPlacementBlockedReason,
}) => {
  const verdict = useMemo(() => buildFitVerdict(testFit), [testFit]);
  const reservations = useMemo(() => fitReservationRows(testFit), [testFit]);

  return (
    <div className="flex h-full flex-col overflow-y-auto" data-testid="studio-verify-rail">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-primary">Generation fit</span>
        <span
          className={`border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${HEADLINE_CLASS[verdict.headline]} rounded-md`}
          data-testid="studio-verify-headline"
        >
          {verdict.headlineLabel}
        </span>
      </div>
      <LandScreeningBasis compact className="border-b border-border px-3 py-2" />
      {onRunMaxPower && (
        <section
          className="mx-3 mb-2 mt-2 rounded-md border border-border-subtle bg-state-hover/40 p-2"
          data-testid="studio-verify-max-power"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-primary">
              Max power here
            </span>
            <button
              type="button"
              onClick={onRunMaxPower}
              disabled={maxPowerBusy}
              data-testid="studio-verify-max-power-run"
              className="rounded-md border border-border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:text-text-primary disabled:opacity-60"
            >
              {maxPowerBusy ? 'Sweeping…' : maxPower ? 'Re-run' : 'Run'}
            </button>
          </div>

          {!maxPower && !maxPowerBusy && (
            <p className="mt-1.5 text-[11px] leading-snug text-text-muted">
              Sweeps capacity upward against this parcel and layout, auto-searching
              rotation and block proportions for a placement that fits.
            </p>
          )}

          {maxPower?.status === 'unavailable' && (
            <p className="mt-1.5 text-[11px] leading-snug text-warning" data-testid="studio-verify-max-power-unavailable">
              {maxPower.reason}
            </p>
          )}

          {maxPower?.status === 'available' && maxPower.maximum && (
            <div className="mt-1.5 space-y-1">
              <p className="text-[12px] font-bold tabular-nums text-text-primary" data-testid="studio-verify-max-power-headline">
                {maxPower.boundQuality === 'search-ceiling-reached' ? '≥ ' : ''}
                {Math.round(maxPower.maximum.firmNetMW)} MW firm
                <span className="ml-1.5 text-[10px] font-normal text-text-muted">
                  → {Math.round(maxPower.maximum.facilityMW)} MW facility
                </span>
              </p>
              <p className="text-[11px] leading-snug text-text-muted" data-testid="studio-verify-max-power-binder">
                {maxPower.bindingStatement}
              </p>

              {/* The placement that made it fit is the actionable part — without
                  it the user is told a capacity is reachable but not how. */}
              {maxPower.recommendedPlacement && onApplyMaxPowerPlacement && (
                <div className="mt-1.5 border-t border-border-subtle pt-1.5">
                  <p className="text-[11px] leading-snug text-text-secondary">
                    Reaching it needs a different placement
                    {' ('}
                    {Object.entries(maxPower.recommendedPlacement.values)
                      .map(([k, v]) => `${k === 'rotationDeg' ? 'rotation' : 'aspect'} ${v}`)
                      .join(', ')}
                    {') — found in '}
                    {maxPower.recommendedPlacement.placementsEvaluated} trials.
                  </p>
                  <p className="mt-0.5 text-[10px] italic leading-snug text-text-muted">
                    This placement is what the MAX-POWER fleet needs. Applying it alone will not
                    change the current fit verdict — the fleet has to be resized to that capacity too.
                  </p>
                  <button
                    type="button"
                    onClick={() => onApplyMaxPowerPlacement(maxPower.recommendedPlacement!.values)}
                    disabled={applyPlacementBlockedReason != null}
                    title={applyPlacementBlockedReason ?? undefined}
                    data-testid="studio-verify-max-power-apply"
                    className="mt-1 rounded-md border border-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apply placement
                  </button>
                  {applyPlacementBlockedReason != null && (
                    <p
                      className="mt-0.5 text-[10px] italic leading-snug text-text-muted"
                      data-testid="studio-verify-max-power-apply-blocked"
                    >
                      {applyPlacementBlockedReason}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {provisionalBasis && (
        <p
          className="mx-3 mb-2 rounded-md border border-warning/40 bg-warning/10 px-2 py-1.5 text-[11px] leading-snug text-warning"
          data-testid="studio-verify-provisional"
        >
          Provisional screening — no equipment defined. The required envelope
          uses default sizing; define equipment in Generation &amp; Impact to
          firm up these checks.
        </p>
      )}

      {/* Go / no-go checklist — each line is a solver verdict. */}
      <ul className="flex flex-col" aria-label="Fit checks">
        {verdict.checks.map((check) => {
          const navigable = onNavigateIssue != null
            && (check.state === 'warn' || check.state === 'unknown');
          const content = (
            <>
              <span className="shrink-0">{STATE_ICON[check.state]}</span>
              <span className="min-w-0 flex-1 text-[11px] text-text-secondary">{check.label}</span>
              <span className="shrink-0 text-[10px] font-bold tabular-nums text-text-primary">{check.metric}</span>
            </>
          );
          return (
            <li
              key={check.id}
              className="border-b border-border/60"
              data-testid={`studio-verify-check-${check.id}`}
              data-state={check.state}
            >
              {navigable ? (
                <button
                  type="button"
                  onClick={() => onNavigateIssue?.(issueNavigation(testFit, check.id))}
                  className="flex min-h-11 w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-warning/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-warning"
                  aria-label={`Review ${check.label}: ${check.metric}`}
                >
                  {content}
                </button>
              ) : (
                <div className="flex min-h-11 items-center gap-2.5 px-3 py-2">
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Utilization + headroom bars. */}
      <div className="flex flex-col gap-3 border-b border-border/60 px-3 py-3">
        <div className="flex flex-col gap-1.5" data-testid="studio-verify-utilization">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-text-muted">
            <span>Packing utilization</span>
            <span className="font-bold tabular-nums text-text-primary">
              {verdict.utilizationPct == null ? '—' : `${verdict.utilizationPct.toFixed(0)}%`}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden border border-border bg-background rounded-md">
            <div className="h-full bg-primary" style={{ width: barWidth(verdict.utilizationPct) }} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5" data-testid="studio-verify-reserve">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-text-muted">
            <span>Parcel headroom</span>
            <span className="font-bold tabular-nums text-text-primary">
              {verdict.greenReservePct == null ? '—' : `${verdict.greenReservePct.toFixed(0)}%`}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden border border-border bg-background rounded-md">
            <div className="h-full bg-primary/60" style={{ width: barWidth(verdict.greenReservePct) }} />
          </div>
        </div>
      </div>

      {/* Reservation acreage breakdown (shared with the status-bar drawer). */}
      <dl className="flex flex-col" data-testid="studio-verify-reservations">
        {reservations.map(row => (
          <div key={row.label} className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-1.5">
            <dt className="text-[10px] text-text-muted">{row.label}</dt>
            <dd className={`text-[10px] font-bold tabular-nums ${row.danger ? 'text-danger' : 'text-text-primary'}`}>{row.value}</dd>
          </div>
        ))}
      </dl>

      {/* The version-safe handoff lives in the Step-6 card; named here, not duplicated. */}
      <p className="mt-auto border-t border-border px-3 py-2.5 text-[9px] leading-relaxed text-text-muted">
        Next: {verdict.nextStep}
      </p>
    </div>
  );
};
